import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import DropdownButton from '@mynewsdesk/ui/lib/DropdownButton';
import MenuItem from '@mynewsdesk/ui/lib/MenuItem';
import './_styles.scss';
import items from './data.json';
import MNDLogo from '../../../../components/Navigation/Logo';
import { getSite, getPathname, getSearchQuery } from 'modules/global/reducers';

const REGIONS = {
  '/dk': 'common_region_dk',
  '/fi': 'common_region_fi',
  '/de': 'common_region_de',
  '/dach': 'common_region_dach',
  '': 'common_region_global',
  '/no': 'common_region_no',
  '/se': 'common_region_se',
  '/us': 'common_region_us',
  '/uk': 'common_region_uk',
};

function siteURL(url, site) {
  if (
    url === 'https://www.mynewsdesk.com{{siteCode}}/mynewsdesk' &&
    ['com', 'uk', 'us'].includes(site.code)
  ) {
    return 'https://www.mynewsdesk.com/com/mynewsdesk';
  }

  if (
    url === 'https://www.mynewsdesk.com{{siteCode}}/blog/' &&
    site.code === 'uk'
  )
    return 'https://www.mynewsdesk.com/blog/';

  return url
    .replace(/{{siteCode}}/g, site.code === 'com' ? '' : site.path)
    .replace(
      /{{langAcademy}}/g,
      site.localized.academy ? `/${site.defaultLocale}` : '/en',
    )
    .replace(
      /{{langHelpCenter}}/g,
      site.localized.helpCenter ? `/${site.defaultLocale}` : '/en',
    );
}

function xURL(site) {
  switch (site.code) {
    case 'dk':
    case 'de':
    case 'fi':
    case 'no':
    case 'se':
    case 'uk':
      return `https://x.com/mynewsdesk_${site.code}`;
    default:
      return 'https://x.com/mynewsdesk';
  }
}

const Footer = ({ t, site, pathname, searchQuery }) => (
  <footer className="mnd-footer">
    <div className="mnd-footer__body">
      {items.map(({ href, i18n, children = [] }, key) => (
        <div className="mnd-footer__item" key={key}>
          <h3 className="mnd-footer-menu__heading">
            {href ? (
              <a href={siteURL(href, site)} className="mnd-footer-menu__link">
                {t(i18n)}
              </a>
            ) : (
              t(i18n)
            )}
          </h3>
          {children.length && (
            <ul className="mnd-footer-menu__body">
              {children.map(({ href, i18n }, key) => (
                <li className="mnd-footer-menu__item" key={key}>
                  {href ? (
                    <a
                      href={siteURL(href, site)}
                      className="mnd-footer-menu__link"
                    >
                      {t(i18n)}
                    </a>
                  ) : (
                    t(i18n)
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
    <hr className="mnd-footer__divider" />
    <aside className="mnd-footer__aside">
      <div className="mnd-footer__social">
        <a
          className="mnd-footer-menu__link"
          href="https://www.facebook.com/Mynewsdesk"
          aria-label="Facebook"
        >
          <i className="fab fa-facebook" />
        </a>
        <a className="mnd-footer-menu__link" href={xURL(site)} aria-label="X">
          <svg
            className="x-logo"
            fill="#ffffff"
            viewBox="0 0 448 512"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm297.1 84L257.3 234.6 379.4 396H283.8L209 298.1 123.3 396H75.8l111-126.9L69.7 116h98l67.7 89.5L313.6 116h47.5zM323.3 367.6L153.4 142.9H125.1L296.9 367.6h26.3z" />
          </svg>
        </a>
        <a
          className="mnd-footer-menu__link"
          href="https://www.linkedin.com/company/mynewsdesk"
          aria-label="LinkedIn"
        >
          <i className="fab fa-linkedin" />
        </a>
      </div>
      <div className="mnd-footer__region">
        <label className="mnd-text-option">
          <span className="mnd-text-option__label">Region:</span>
          <DropdownButton
            title={t(`${REGIONS[site.path]}`)}
            id="dropdown-regions"
            dropup={true}
            onSelect={key =>
              (window.location.href = `https://www.mynewsdesk.com${key}/stories${pathname}${searchQuery}`)
            }
          >
            {Object.keys(REGIONS).map(key => (
              <MenuItem eventKey={key} key={key} active={site.path === key}>
                {t(`${REGIONS[key]}`)}
              </MenuItem>
            ))}
          </DropdownButton>
        </label>
      </div>
      <div className="mnd-footer__logo">
        <a className="mnd-footer-menu__link" href="https://www.mynewsdesk.com">
          <span className="screen-reader-only">Go to Mynewsdesk.com</span>
          <MNDLogo red />
        </a>
      </div>
    </aside>
  </footer>
);

Footer.propTypes = {
  t: PropTypes.func.isRequired,
  site: PropTypes.shape({
    code: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    defaultLocale: PropTypes.string.isRequired,
    localized: PropTypes.shape({
      academy: PropTypes.bool,
      helpCenter: PropTypes.bool,
    }),
  }).isRequired,
  pathname: PropTypes.string.isRequired,
  searchQuery: PropTypes.string,
};

export default connect(state => ({
  searchQuery: getSearchQuery(state),
  site: getSite(state),
  pathname: getPathname(state),
}))(withNamespaces()(Footer));
